import React, { useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { Milestone, Project, ProjectContractStatus } from '../types';
import {
  getStatusColor,
  getStatusLabel,
  type StatusStep,
} from '../services/projectStatusUtils';
import { projectDisplayStatus } from '../utils/milestoneWorkflowStatus';
import {
  CONTRACT_STATUS_COLOR,
  singleContractStatus,
  sortedContractStatuses,
  strongestContractStatus,
} from '../utils/projectContractStatus';
import { useAuth } from '../context/AuthContext';
import { useContractStatusStyle } from '../hooks/useContractStatusStyle';
import Modal from './Modal';

interface ProjectWorkflowStatusBadgeProps {
  project: Pick<Project, 'status' | 'workflowStatus'> &
    Partial<Pick<Project, 'id' | 'projectNumber' | 'contractStatuses'>>;
  milestones?: Milestone[];
  steps?: StatusStep[];
  className?: string;
  testId?: string;
}

/**
 * One status badge for every persona. The optional milestones give detail
 * views immediate local updates; list views use the API-derived workflowStatus.
 *
 * With several contracts the badge shows the most urgent contract's status and
 * "+N" when other contracts are in another state; "+N" opens every contract's
 * status (ClickUp 869fbrpjk). One contract keeps the usual project status,
 * except that "not started" gives way to a missing signature or payment.
 * "Stacked brickor" on the profile page shows the other statuses as a stack
 * under the badge instead. Both fan the other statuses out under the badge on
 * hover or keyboard focus.
 */
export function ProjectWorkflowStatusBadge({
  project,
  milestones = [],
  steps,
  className = '',
  testId,
}: ProjectWorkflowStatusBadgeProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [fanAt, setFanAt] = useState<{ left: number; top: number } | null>(null);
  const badgeRef = useRef<HTMLSpanElement>(null);
  const style = useContractStatusStyle();
  const strongest = strongestContractStatus(project.contractStatuses);

  if (!strongest) {
    const status = projectDisplayStatus(project, milestones);
    const contractStatus = singleContractStatus(status, project.contractStatuses);
    if (contractStatus) {
      return (
        <span
          className={`badge bg-${CONTRACT_STATUS_COLOR[contractStatus]} ${className}`.trim()}
          data-testid={testId}
          data-project-workflow-status={contractStatus}
        >
          {t(`projectContractStatus.status.${contractStatus}`)}
        </span>
      );
    }
    return (
      <span
        className={`badge bg-${getStatusColor(status, steps)} ${className}`.trim()}
        data-testid={testId}
        data-project-workflow-status={status}
      >
        {getStatusLabel(status, steps)}
      </span>
    );
  }

  const label = t(`projectContractStatus.status.${strongest.status}`);
  const modal = open && createPortal(
    <ProjectContractStatusModal
      project={project}
      strongestLabel={label}
      statuses={project.contractStatuses ?? []}
      onClose={() => setOpen(false)}
    />,
    document.body
  );

  // Hover or keyboard focus on "+N" or the stack fans the other statuses out under the badge.
  const others = sortedContractStatuses(project.contractStatuses).filter(item => item.status !== strongest.status);
  const showFan = () => {
    const rect = badgeRef.current?.getBoundingClientRect();
    if (rect) setFanAt({ left: rect.left, top: rect.top });
  };
  const fanHandlers = {
    onClick: () => { setFanAt(null); setOpen(true); },
    onMouseEnter: showFan,
    onMouseLeave: () => setFanAt(null),
    onFocus: (event: React.FocusEvent<HTMLButtonElement>) => { if (event.currentTarget.matches(':focus-visible')) showFan(); },
    onBlur: () => setFanAt(null),
  };
  const fan = fanAt && createPortal(<ProjectStatusFan at={fanAt} statuses={others} />, document.body);

  if (style === 'stack' && strongest.others > 0) {
    return (
      <span className="project-status-group" onClick={event => event.stopPropagation()}>
        <button
          type="button"
          className="project-status-stack"
          aria-label={t('projectContractStatus.stackAria', {
            status: label,
            others: others.map(item => t(`projectContractStatus.status.${item.status}`).toLocaleLowerCase()).join(', ')
          })}
          aria-haspopup="dialog"
          {...fanHandlers}
          data-testid="project-status-stack"
        >
          <span
            ref={badgeRef}
            className={`badge bg-${CONTRACT_STATUS_COLOR[strongest.status]} ${className}`.trim()}
            data-testid={testId}
            data-project-workflow-status={strongest.status}
          >
            {label}
          </span>
          {others.slice(0, 3).map((item, index) => (
            <span key={item.contractId} className={`project-status-layer is-layer-${index + 1} is-${CONTRACT_STATUS_COLOR[item.status]}`} aria-hidden="true" />
          ))}
        </button>
        {fan}
        {modal}
      </span>
    );
  }

  return (
    // Clicks stay here: in a list the badge sits in a row that opens the project.
    <span className="project-status-group" onClick={event => event.stopPropagation()}>
      <span
        ref={badgeRef}
        className={`badge bg-${CONTRACT_STATUS_COLOR[strongest.status]} ${className}`.trim()}
        data-testid={testId}
        data-project-workflow-status={strongest.status}
      >
        {label}
      </span>
      {strongest.others > 0 && (
        <button
          type="button"
          className="project-status-more"
          aria-label={t('projectContractStatus.moreAria', { count: strongest.others })}
          aria-haspopup="dialog"
          {...fanHandlers}
          data-testid="project-status-more"
        >
          <span>{t('projectContractStatus.more', { count: strongest.others })}</span>
        </button>
      )}
      {fan}
      {/* On the page itself, so it inherits nothing from the table cell it opens from. */}
      {modal}
    </span>
  );
}

/** The stack fanned out under the badge: each other contract's status and number. */
function ProjectStatusFan({ at, statuses }: { at: { left: number; top: number }; statuses: ProjectContractStatus[] }) {
  const { t } = useTranslation();
  const [spread, setSpread] = useState(false);
  const [left, setLeft] = useState(at.left);
  const fan = useRef<HTMLDivElement>(null);
  // Under the badge, but inside the screen: the widest item decides how far left it starts.
  React.useLayoutEffect(() => {
    const widest = Math.max(0, ...Array.from(fan.current?.children ?? []).map(item => (item as HTMLElement).offsetWidth));
    setLeft(Math.max(8, Math.min(at.left, window.innerWidth - widest - 8)));
  }, [at.left]);
  React.useEffect(() => {
    const frame = requestAnimationFrame(() => setSpread(true));
    return () => cancelAnimationFrame(frame);
  }, []);
  return (
    <div
      ref={fan}
      className={`project-status-fan${spread ? ' is-open' : ''}`}
      style={{ left, top: at.top }}
      aria-hidden="true"
      data-testid="project-status-fan"
    >
      {statuses.map((item, index) => (
        <span key={item.contractId} className={`project-status-fan-item is-item-${Math.min(index + 1, 6)}`}>
          <span className={`badge bg-${CONTRACT_STATUS_COLOR[item.status]}`}>{t(`projectContractStatus.status.${item.status}`)}</span>
          {t(item.contractType === 'variation' ? 'projectContractStatus.ata' : 'projectContractStatus.contract', { number: item.contractNumber })}
        </span>
      ))}
    </div>
  );
}

function ProjectContractStatusModal({ project, strongestLabel, statuses, onClose }: {
  project: Partial<Pick<Project, 'id' | 'projectNumber'>>;
  strongestLabel: string;
  statuses: ProjectContractStatus[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const base = user?.isAdmin ? '/admin/projects'
    : user?.personaType === 'person' ? '/person/projects'
      : user?.personaType === 'lawyer' ? '/lawyer/projects'
        : user?.personaType === 'inspector' ? '/inspector/projects'
          : '/projects';
  const contractName = (item: ProjectContractStatus) => {
    const number = t(item.contractType === 'variation' ? 'projectContractStatus.ata' : 'projectContractStatus.contract', { number: item.contractNumber });
    return item.contractName?.trim() ? `${number} · ${item.contractName.trim()}` : number;
  };
  return (
    <Modal
      title={project.projectNumber != null
        ? t('projectContractStatus.title', { number: project.projectNumber })
        : t('projectContractStatus.titleNoNumber')}
      onClose={onClose}
      fullscreenBelow="sm"
      footer={<button type="button" className="btn btn-light" onClick={onClose}>{t('common.close')}</button>}
    >
      <p className="text-muted small mb-2">{t('projectContractStatus.intro', { status: strongestLabel.toLocaleLowerCase() })}</p>
      <ul className="project-contract-status-list list-unstyled mb-0" data-testid="project-contract-status-list">
        {sortedContractStatuses(statuses).map(item => (
          <li key={item.contractId}>
            {project.id
              ? <Link to={`${base}/${project.id}?tab=contract&contractId=${item.contractId}`} onClick={onClose}>{contractName(item)}</Link>
              : <span>{contractName(item)}</span>}
            <span className={`badge bg-${CONTRACT_STATUS_COLOR[item.status]}`}>{t(`projectContractStatus.status.${item.status}`)}</span>
          </li>
        ))}
      </ul>
    </Modal>
  );
}
